<template>
    <component
        :is="descriptor.component"
        v-if="field?.id"
        :id="field.id"
        ref="field"
        v-model="value"
        class="app-field"
        :label="computedLabel"
        :name="field.id"
        :disabled="isDisabled"
        v-bind="attributes"
        :required="computedRequired"
        :maxlength="maxLength"
        :minlength="minLength"
        :max="computedMax"
        :min="computedMin"
        :validations="computedValidations"
        v-on="$listeners"
    >
        <template #label
            ><slot name="label">{{ computedLabel }}</slot></template
        >

        <template #message="messageProps">
            <slot name="message" :message="messageProps.message" />
        </template>
    </component>
</template>

<script>
    import { getFieldDescriptor } from "@/components/form/FieldDescriptorFactory"
    import { fillProperty, getProperty } from "@/util/object"
    import { maxAge, minAge, validChars } from "@/util/validation"
    import { proveState } from "@/state"

    const builtInRules = new Set(["min", "max", "minLength", "maxLength", "required", "validOptions"])

    export default {
        inject: {
            vcFieldGroup: { from: "vcFieldGroup", default: null },
        },
        inheritAttrs: false,
        props: {
            application: Object,
            field: Object,
            data: Object,
            labelCode: String,
            label: String,
            disabled: {
                type: Boolean,
                default: undefined,
            },
            makeOptional: Boolean,
            required: Boolean,
            max: [Number, Date],
            min: [Number, Date],
            // value: {},
            type: {
                type: String,
                default: "text",
            },

            formSettings: {
                type: Object,
                default: () => {
                    return {}
                },
            },

            validations: {
                type: Object,
                default: () => {
                    return {}
                },
            },
        },

        computed: {
            descriptor() {
                return getFieldDescriptor({ type: this.type })
            },

            computedLabel() {
                return this.resolvedLabel || ""
            },

            computedValidations() {
                const validations = { ...this.validations }

                Object.keys(this.field.rules).forEach(id => {
                    if (validations[id]) return
                    if (builtInRules.has(id)) return
                    const validator = createValidatorFromRule(this.field.rules[id])
                    if (validator) validations[id] = validator
                })

                if (this.field.rules["requireOne"]) {
                    validations["requireOne"] = this.field.rules["requireOne"]?.fields
                }

                return validations
            },

            isDisabled() {
                return (
                    this.disabled ??
                    (this.formSettings.readOnly || proveState.prefilledFields.includes(this.field.path))
                )
            },

            resolvedLabel() {
                if (this.label) return this.label
                if (this.labelCode) return this.$t(this.labelCode)
                return this.$t(`field.${this.field.id}`)
            },

            computedRequired() {
                if (this.makeOptional) return false

                if (this.required) {
                    return true
                }

                if (this.field.rules["required"]) {
                    return true
                }

                return false
            },

            requireOne() {
                return this.field.rules["requireOne"]?.fields
            },

            minLength() {
                return this.field.rules["minLength"]?.length
            },

            maxLength() {
                return this.field.rules["maxLength"]?.length
            },

            computedMin() {
                if (this.min) return this.min

                return this.field.type === "local_date"
                    ? this.field.rules["minDate"]?.value
                    : this.field.rules["min"]?.value
            },

            computedMax() {
                if (this.max) return this.max

                return this.field.type === "local_date"
                    ? this.field.rules["maxDate"]?.value
                    : this.field.rules["max"]?.value
            },

            attributes() {
                const attrs = {
                    ...this.$attrs,
                }

                if (this.type === "dynamicPhone") {
                    attrs.phoneTypes = this.field.rules["validOptions"]?.options || []
                    attrs.homePhone = getProperty(this.application, this.$attrs.homePhoneField?.path)
                    attrs.mobilePhone = getProperty(this.application, this.$attrs.mobilePhoneField?.path)
                } else if (this.type === "select" || this.type === "radio") {
                    attrs.options = this.field.rules["validOptions"]?.options || []
                    attrs.nameField = "label"
                }

                return attrs
            },

            value: {
                get() {
                    return getProperty(this.application, this.field.path)
                },
                set(value) {
                    if (this.type === "dynamicPhone") {
                        const homePhoneField = this.$attrs.homePhoneField
                        const mobilePhoneField = this.$attrs.mobilePhoneField
                        this.setFieldValue(this.field, value?.type)

                        if (value?.type === "mobile") {
                            this.setFieldValue(mobilePhoneField, value?.phone)
                            this.setFieldValue(homePhoneField, "")
                        } else if (value?.type === "home") {
                            this.setFieldValue(homePhoneField, value?.phone)
                            this.setFieldValue(mobilePhoneField, "")
                        }
                    } else {
                        this.setFieldValue(this.field, value)
                    }
                },
            },
        },

        created() {
            if (this.vcFieldGroup) {
                this.vcFieldGroup.registerField(this.field)
            }
        },

        methods: {
            setFieldValue(field, value) {
                fillProperty(this.application, field.path, value, field.name)
            },

            focus() {
                this.$refs.field.focus()
            },

            scrollIntoView(opts) {
                this.$refs.field.scrollIntoView(opts)
            },

            validate() {
                this.$refs.field.validate()
            },
        },
    }

    function createValidatorFromRule(rule) {
        switch (rule?.id) {
            case "maxAge":
                return maxAge(rule.age)
            case "minAge":
                return minAge(rule.age)
            case "noPoBox":
                return true
            case "validChars":
                return validChars(rule.validChars)
            default:
                return null
        }
    }
</script>
